<template>
  <v-menu
    v-if="!!globalStore.pageCount && globalStore.pageCount > 1"
    v-model="menuModel"
    class="move-to-page-menu"
    :close-on-content-click="false"
    location="bottom"
    @close="closeMenu"
  >
    <!-- Activator -->
    <template #activator="{ props: ActivatorProps }">
      <a
        v-bind="ActivatorProps"
        class="deep-text-button"
        data-test-id="move-signature"
      >
        {{ menuLabel }}
      </a>
    </template>
    <!-- /Activator -->

    <v-card
      class="move-to-page-menu"
      :min-width="
        signaturesPresentOnDifferentPages && !confirmed && !inProgress
          ? 300
          : 250
      "
    >
      <!-- User confirmation is needed -->
      <v-list-item
        v-if="
          signaturesPresentOnDifferentPages &&
          !confirmed &&
          !inProgress &&
          !modelValue
        "
        class="pr-1 d-flex justify-lg-space-between align-center py-2"
        density="compact"
      >
        <div class="confirm-hint">
          <I18nT keypath="texts.signatures_on_different_pages_warning">
            <template #pageNumber>
              <strong>{{ globalStore.currentPage }}</strong>
            </template>
          </I18nT>
          <p class="text-primary cursor-pointer">
            <a @click="onConfirm">
              {{ i18n.t('buttons.confirm') }}
            </a>
          </p>
        </div>
        <template #append>
          <v-divider vertical />
          <v-btn class="ml-2" icon size="x-small" @click="closeMenu">
            <v-icon color="grey-lighten-1" size="small"> far fa-xmark </v-icon>
          </v-btn>
        </template>
      </v-list-item>
      <!-- Signatures are all on the same page or user confirmed -->
      <v-list-item v-else class="page-selector" density="compact">
        <div class="d-flex flex-row justify-space-between align-center">
          <v-btn
            color="primary"
            data-test-id="move-to-first-page"
            :disabled="!canDecrease"
            icon
            size="x-small"
            variant="plain"
            @click="placeOnFirstPage"
          >
            <v-icon size="medium"> far fa-chevrons-left </v-icon>
          </v-btn>

          <v-btn
            color="primary"
            data-test-id="move-to-previous-page"
            :disabled="!canDecrease"
            icon
            size="x-small"
            variant="plain"
            @click="decreasePageNumber"
          >
            <v-icon size="medium"> far fa-chevron-left </v-icon>
          </v-btn>

          <div class="px-4">{{ i18n.t('labels.page') }}</div>

          <v-text-field
            v-model="pageNumber"
            density="compact"
            hide-details
            hide-spin-buttons
            :max="globalStore.pageCount"
            min="1"
            required
            :rules="pageNumberRules"
            single-line
            type="number"
            variant="outlined"
          />

          <v-btn
            color="primary"
            data-test-id="move-to-next-page"
            :disabled="!canIncrease"
            icon
            size="x-small"
            variant="plain"
            @click="increasePageNumber"
          >
            <v-icon size="medium"> far fa-chevron-right </v-icon>
          </v-btn>

          <v-btn
            color="primary"
            data-test-id="move-to-last-page"
            :disabled="!canIncrease"
            icon
            size="x-small"
            variant="plain"
            @click="placeOnLastPage"
          >
            <v-icon size="medium"> far fa-chevrons-right </v-icon>
          </v-btn>
        </div>
      </v-list-item>
      <!-- /Signatures are all on the same page or user confirmed -->
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  // eslint-disable-next-line vue/no-deprecated-model-definition
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  }
});
</script>

<script setup lang="ts">
import { useGlobalStore } from '@/stores/global.ts';
import { computed, onUnmounted, ref, watch } from 'vue';
import { useEmitter } from '@/composables/useEmitter.ts';
import { useI18n } from 'vue-i18n';
import I18nT from '@/components/I18nT.vue';
import { debounce } from '@/composables/useUtils';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { SigneeType } from '@/types/enums/SigneeType';
import { useCreateStore } from '@/stores/create';
import { useSignStore } from '@/stores/sign';
import {
  injectStrict,
  PlaceHolderComposableKey
} from '@/composables/placement/placementSymbols.ts';
import {
  persistAutoPlacedSignStepPlaceholders,
  persistSigneePlaceholders,
  scrollToPlaceholder
} from '@/composables/placement/usePlacementUtility.ts';
import type { Placeholder } from '@/composables/placement/placementTypes.ts';
import {
  canSigneeModifyAutographPositionOnFile,
  isPlaceholderForSignee
} from '@/utils/signeeSignings.ts';
import { useRouter } from 'vue-router';
import type { Signee } from '@/types/Signee';

const props = defineProps({
  modelValue: {
    type: Object,
    required: false,
    default: undefined
  }
});

const { currentDocument, isDraft, shouldPersistSignPlacement } =
  useDocumentContext();
const createStore = useCreateStore();
const signStore = useSignStore();
const globalStore = useGlobalStore();
const router = useRouter();
const emitter = useEmitter();
const i18n = useI18n();

const confirmed = ref(false);
const pageNumber = ref();
const menuModel = ref(false);
const inProgress = ref(false);

const {
  movePlaceHolders,
  currentPlaceholders,
  placeholderTemplateRefs,
  requestPlaceholderRemount
} = injectStrict(PlaceHolderComposableKey);

// Whether the sign-step envelope override (`shouldPersistSignPlacement`:
// envelope + sign route + isSignee + no quorum for the signee's group) also
// actually grants the signee being moved (`props.modelValue` — this menu is
// only ever bound to a single signee's own signature on the sign step, never
// "move all") permission to move a signature on the currently selected file.
// Mirrors `canPlaceOnSignStep` in `SigningCard.vue`/`PdfToolbar.vue`.
const canPlaceOnSignStep = computed(() => {
  const signee = props.modelValue as Signee | undefined;
  if (!shouldPersistSignPlacement.value || !signee) return false;
  return canSigneeModifyAutographPositionOnFile(
    signee,
    globalStore.selectedDocumentFileId,
    currentDocument.value
  );
});

const signees = computed(() =>
  isDraft.value
    ? currentDocument.value?.signeesOrdered
        .flat()
        .filter((signee) => signee.signeeType === SigneeType.SIGN)
    : currentDocument.value?.signees
);

const pageNumberRules = computed(() => {
  return [
    (v) => !!v, // Required
    (v) => /^[1-9]\d*$/.test(v), // Positive integer
    (v) => isValid(v) // Is a valid page Number
  ];
});

const canIncrease = computed(() => {
  if (pageNumber.value) {
    return pageNumber.value < globalStore.pageCount;
  } else {
    return false;
  }
});

const canDecrease = computed(() => {
  if (pageNumber.value) {
    return pageNumber.value > 1;
  } else {
    return false;
  }
});

const signaturesPresentOnDifferentPages = computed(() => {
  if (isDraft.value) {
    const unique = [
      ...new Set(
        (currentPlaceholders.value as Placeholder[]).map(
          (item: Placeholder) => {
            if (item.position) {
              return item.position.pageNumber;
            }
          }
        )
      )
    ];
    return unique.length > 1;
  }

  if (signees.value) {
    const unique = [
      ...new Set(
        signees.value.map((item) => {
          if (item.autographPosition) {
            return item.autographPosition.pageNumber;
          }
        })
      )
    ];
    return unique.length > 1;
  } else {
    return false;
  }
});

const numericPageNumber = computed(() => {
  if (pageNumber.value) {
    return pageNumber.value;
  } else {
    return undefined;
  }
});

const menuLabel = computed(() => {
  if (router.currentRoute.value.name === 'document-seal')
    return i18n.t('texts.move_one_seal');
  return props?.modelValue
    ? i18n.t('texts.move_one_signature')
    : i18n.t('texts.move_all_signatures');
});

emitter.$on('close-move-all-signatures-menu', closeMenu);

onUnmounted(() => {
  emitter.$off('close-move-all-signatures-menu', closeMenu);
});

function isPageNumberValid(value: number | null) {
  if (!value || (value && value > globalStore.pageCount)) return false;
  return value >= 1;
}

function isValid(pageNumber: number) {
  return pageNumber <= globalStore.pageCount;
}

function increasePageNumber() {
  if (numericPageNumber.value) {
    pageNumber.value = numericPageNumber.value + 1;
  }
}

function placeOnFirstPage() {
  pageNumber.value = 1;
}

function placeOnLastPage() {
  pageNumber.value = globalStore.pageCount;
}

function decreasePageNumber() {
  if (numericPageNumber.value) {
    pageNumber.value = numericPageNumber.value - 1;
  }
}

function closeMenu() {
  menuModel.value = false;
  confirmed.value = false;
}

const updateDebounced = debounce(function (
  fromPage,
  toPage,
  placeholdersToMove
) {
  updateSignaturesPage(
    parseInt(fromPage),
    parseInt(toPage),
    placeholdersToMove
  );
}, 300);

async function updateSignaturesPage(
  fromPage: number,
  toPage: number,
  placeholdersToUpdate: Placeholder[]
) {
  if (isPageNumberValid(pageNumber.value)) {
    // Set in progress so that we do not show the warning in menu that signatures are at the moment on 2 different pages
    inProgress.value = true;
    await movePlaceHolders(fromPage, toPage, placeholdersToUpdate);

    if (placeholdersToUpdate && placeholdersToUpdate.length > 0) {
      scrollToPlaceholder(placeholdersToUpdate[0] as Placeholder, true);
    }
    // Filter to only make calls for the needed signatures
    if (placeholdersToUpdate) {
      if (isDraft.value && currentDocument.value) {
        await persistSigneePlaceholders(
          createStore,
          placeholdersToUpdate,
          placeholderTemplateRefs?.value,
          createStore.filteredSignees,
          currentDocument.value
        );
      } else if (canPlaceOnSignStep.value) {
        await persistAutoPlacedSignStepPlaceholders(
          signStore,
          props.modelValue as Signee,
          placeholdersToUpdate,
          currentPlaceholders.value as Placeholder[],
          placeholderTemplateRefs?.value,
          requestPlaceholderRemount
        );
      }
      inProgress.value = false;
      confirmed.value = false;
    }
  }
}

async function onConfirm() {
  confirmed.value = true;
  pageNumber.value = globalStore.currentPage;
  const placeholdersToMove: Placeholder[] = (
    currentPlaceholders.value as Placeholder[]
  ).filter(
    (placeholder: Placeholder) =>
      placeholder.position?.pageNumber !== globalStore.currentPage &&
      placeholder.isDraggable
  );

  updateDebounced(
    placeholdersToMove[0]?.position?.pageNumber || globalStore.currentPage,
    globalStore.currentPage,
    placeholdersToMove
  );
}

watch(
  () => menuModel.value,
  (newValue) => {
    const match = (currentPlaceholders.value as Placeholder[]).find(
      (placeholder) =>
        props.modelValue?.signeeId &&
        isPlaceholderForSignee(
          placeholder,
          props.modelValue.signeeId,
          currentDocument.value?.signeeGroups
        ) &&
        placeholder.isPlaced &&
        placeholder.isVisible
    );
    if (props.modelValue && match && match.position) {
      pageNumber.value = match.position.pageNumber;
      return;
    }

    // If the menu has been opened for the first time the page wont be set
    // we need to default to the page where signatures are already placed
    if (
      newValue &&
      !pageNumber.value &&
      (!signaturesPresentOnDifferentPages.value || confirmed.value)
    ) {
      // On some screens the page number can change after we
      // automatically place the signatures since it will scroll the window down
      // this will cause the menu opening to move the signatures automatically to the second page
      // and to get around that case, instead of taking the current page from the store
      // we can just get the page from one of the signees since they will all be on the same page
      if (
        currentPlaceholders.value &&
        (currentPlaceholders.value as Placeholder[]).length > 0
      ) {
        const firstVisiblePlaceholder = (
          currentPlaceholders.value as Placeholder[]
        ).find((placeholder) => placeholder.isVisible && placeholder.isPlaced);
        if (!firstVisiblePlaceholder) return;
        pageNumber.value = firstVisiblePlaceholder!.position!.pageNumber;
        return;
      }
    } else if (
      currentPlaceholders.value &&
      (currentPlaceholders.value as Placeholder[]).length === 1
    ) {
      pageNumber.value = currentPlaceholders.value[0].position.pageNumber;
    } else {
      confirmed.value = false;
    }
  }
);

watch(
  () => pageNumber.value,
  (newValue, oldValue) => {
    if (newValue) {
      if (
        props.modelValue &&
        newValue !== oldValue &&
        oldValue &&
        menuModel.value
      ) {
        const placeholdersToMove = (
          currentPlaceholders.value as Placeholder[]
        ).filter(
          (placeholder: Placeholder) =>
            placeholder.isVisible &&
            placeholder.isPlaced &&
            placeholder.isDraggable
        );
        updateDebounced(oldValue, newValue, placeholdersToMove);
      } else if (menuModel.value && oldValue) {
        const placeholdersToMove = signaturesPresentOnDifferentPages.value
          ? (currentPlaceholders.value as Placeholder[]).filter(
              (placeholder: Placeholder) =>
                placeholder.position?.pageNumber !== globalStore.currentPage &&
                placeholder.isDraggable
            )
          : (currentPlaceholders.value as Placeholder[]).filter(
              (placeholder: Placeholder) =>
                placeholder.isVisible &&
                placeholder.isPlaced &&
                placeholder.isDraggable
            );

        updateDebounced(oldValue, newValue, placeholdersToMove);
      }
    }
  }
);
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/styles/settings.scss' as *;

.confirm-hint {
  margin-right: 4px;
  max-width: 200px;
}

.page-selector {
  height: 58px;
}
</style>
